import React, { useState, useEffect } from 'react';
import { Button, notification, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import moment from 'moment';
import Table from '../../../../Template/layoutComponents/Table';
import { getInstruments, deleteInstrument } from './services/instrument.service.ts';

const InstrumentsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { plantId } = useParams();
  const { instance } = useMsal();

  const [instruments, setInstruments] = useState({ items: [], totalItems: 0 });
  const [notificationApi, contextHolder] = notification.useNotification();
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [deletingItem, setDeletingItem] = useState(null);

  const enrichItems = (items) =>
    items.map((it) => {
      const isExpired =
        it.certificate_expiration_date &&
        moment(it.certificate_expiration_date).isBefore(moment(), 'day');
      return {
        ...it,
        certificate_status: isExpired ? (t('expired') || 'Vencida') : (t('active') || 'Ativa'),
        out_of_use: isExpired ? (t('yes') || 'Sim') : (t('no') || 'Não'),
      };
    });

  const loadInstruments = async () => {
    try {
      setLoading(true);
      const data = await getInstruments(instance, { plantId });
      setInstruments({ ...data, items: enrichItems(data.items) });
    } catch (error) {
      console.error(error);
      notificationApi.error({
        message: t('error') || 'Erro',
        description: t('load_failed') || 'Falha ao carregar instrumentos',
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInstruments();
  }, [plantId]);

  const handleDelete = (item) => {
    Modal.confirm({
      title: t('confirm_delete') || 'Confirmar exclusão',
      icon: <ExclamationCircleOutlined />,
      content: `${t('delete_confirmation') || 'Tem certeza que deseja excluir o instrumento'} "${item.name}"?`,
      okText: t('delete') || 'Excluir',
      okType: 'danger',
      cancelText: t('cancel') || 'Cancelar',
      onOk: async () => {
        try {
          setDeletingItem(item._id || item.id);
          await deleteInstrument(instance, plantId, item._id || item.id);
          notificationApi.success({
            message: t('success') || 'Sucesso',
            description: t('instrument_deleted_successfully') || 'Instrumento excluído com sucesso',
            duration: 3,
          });
          loadInstruments();
          setSelectedItems([]);
        } catch (error) {
          console.error(error);
          notificationApi.error({
            message: t('error') || 'Erro',
            description:
              error?.response?.data?.message ||
              t('delete_failed') ||
              'Falha ao excluir instrumento',
            duration: 5,
          });
        } finally {
          setDeletingItem(null);
        }
      },
    });
  };

  return (
    <div style={{ padding: 24, background: '#fafafa', minHeight: '100vh' }}>
      {contextHolder}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          padding: 24,
          background: 'white',
          borderRadius: 8,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
            {t('instruments') || 'Instrumentos'}
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>
            Gerencie os instrumentos desta planta
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          {selectedItems.length === 1 && (
            <Button
              danger
              size='large'
              icon={<DeleteOutlined />}
              loading={deletingItem === (selectedItems[0]._id || selectedItems[0].id)}
              onClick={() => handleDelete(selectedItems[0])}
            >
              {t('delete') || 'Excluir'}
            </Button>
          )}
          <Button
            type='primary'
            size='large'
            icon={<PlusOutlined />}
            onClick={() => navigate(`/maintenance/plants/${plantId}/instruments/form`)}
          >
            {t('new_instrument') || 'Novo Instrumento'}
          </Button>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: 8 }}>
        <Table
          handleRowClick={(row) =>
            navigate(`/maintenance/plants/${plantId}/instruments/form/${row._id || row.id}`)
          }
          handleSelect={(selected) => setSelectedItems(selected)}
          apiCall={async (inst, params) => {
            const data = await getInstruments(inst, { ...params, plantId });
            return { ...data, items: enrichItems(data.items) };
          }}
          items={instruments.items}
          startingTotalItems={instruments.totalItems || instruments.total}
          tableSettings={{
            columnsConfig: {
              name: { label: t('name') || 'Nome' },
              category: {
                label: t('category') || 'Categoria',
                replaceValue: (v) => ({ label: v?.name || '-', id: v?._id || '' }),
              },
              certificate_status: { label: t('certificate_status') || 'Status do Certificado' },
              out_of_use: { label: t('out_of_use') || 'Fora de Uso' },
            },
            sortable: ['name', 'certificate_status'],
            listFilter: ['category', 'certificate_status'],
            searchFilter: ['name'],
            uniqueValues: instruments.uniqueValues,
          }}
        />
      </div>
    </div>
  );
};

export default InstrumentsList;
